import { useState } from 'react';
import type { MemberCtx } from './MemberHome';
import { callFirstOf } from './memberData';
import { facts, type SheetRow } from './Sheet';
import { Act, D, S } from './ui';
import { useCustomerText } from '../../ui/buildLabels';

// Emergency (design T11512-11567, hTabHelp). The ladder, its waits, the practice run and
// the planning list wait on the PROPOSED GET /athome/me/:id/emergency; "Get help now"
// and every change are member actions. A failed "Get help now" says to use the pendant
// or call 911, never just "not sent".

const BTN = (primary: boolean) =>
  'display:inline-flex;align-items:center;height:44px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;' +
  (primary ? 'background:#2F5C97;color:#fff;' : 'color:#3C454F;border:1px solid rgba(34,39,46,.18);');
const CHIP = 'font-size:16px;font-weight:700;letter-spacing:.07em;border-radius:6px;padding:4px 10px;flex:0 0 auto;white-space:nowrap;color:#264C7E;background:rgba(79,138,214,.2);border:1px solid rgba(47,92,151,.34);';
const STEP_BTN = 'display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;flex:0 0 44px;border-radius:10px;border:1px solid rgba(34,39,46,.16);background:#FBF8F1;color:#3C454F;font-size:16px;font-weight:700;text-decoration:none;';
const markCss = (ok: boolean) =>
  'display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;flex:0 0 22px;border-radius:5px;font-size:16px;font-weight:700;' +
  (ok ? 'background:rgba(47,138,111,.2);color:#12503C;' : 'background:rgba(34,39,46,.07);border:1px solid rgba(34,39,46,.2);color:transparent;');

export function EmergencyTab({ c }: { c: MemberCtx }) {
  const plain = useCustomerText();
  const e = c.d.emergency.data;
  const people = c.d.people.data?.people ?? [];
  const callFirst = callFirstOf(c.d.people.data, c.d.me.data?.primaryContact ?? null);
  const [tested, setTested] = useState(false);
  const ladder = e?.ladder ?? [];
  const to911 = ladder.length ? ladder[ladder.length - 1].waitMinutes : null;

  const test = () => {
    if (c.readOnly || !e) return;
    c.act({ kind: 'ladder_test' })
      .then((done) => {
        setTested(true);
        c.setToast(done);
      })
      .catch((err: Error) => c.setToast(`Not sent: ${err.message}.`));
  };
  const bump = (i: number, dMin: number) => {
    if (c.readOnly) return;
    const next = Math.max(0, ladder[i].waitMinutes + dMin);
    c.act({ kind: 'ladder_wait', target: String(i), value: next })
      .then(() => c.d.emergency.reload())
      .catch((err: Error) => c.setToast(`Not changed: ${err.message}.`));
  };

  return (
    <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:10px;min-width:0;align-items:start;">
      <Act
        css="grid-column:1/-1;box-sizing:border-box;display:flex;align-items:center;gap:18px;flex-wrap:wrap;row-gap:10px;min-height:104px;padding:18px 22px;border-radius:18px;background:#B3261E;color:#FCFAF4;text-decoration:none;animation:riseIn .46s cubic-bezier(.22,.61,.36,1);transition:background .16s ease;"
        testId="get-help-now"
        onClick={() =>
          c.openSheet({
            kicker: 'EMERGENCY',
            title: 'Get help now',
            glyph: '!',
            color: '#B3261E',
            body: 'This starts the ladder straight away. Nobody will be annoyed. If you pressed it by mistake you can stop it on the next screen.',
            rows: facts(people.slice(0, 3).map((p, i) => [`Step ${i + 1}`, `${p.name} · ${p.role}`])) as SheetRow[],
            primary: 'Yes, get help',
            onPrimary: async () => {
              try {
                return await c.act({ kind: 'sos' });
              } catch {
                return 'This page could not start the ladder. Press and hold your pendant, or call 911 now.';
              }
            },
          })
        }
      >
        <S css="flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:56px;height:56px;border-radius:18px;background:rgba(252,250,244,.18);">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6.4 3.7h3.1l1.6 3.9-2 1.2a11 11 0 0 0 5 5l1.2-2 3.9 1.6v3.1a1.6 1.6 0 0 1-1.6 1.6A15.5 15.5 0 0 1 4.8 5.3a1.6 1.6 0 0 1 1.6-1.6Z" />
          </svg>
        </S>
        <S css="flex:1 1 200px;min-width:0;">
          <S css="display:block;font-size:28px;font-weight:700;letter-spacing:-.025em;line-height:1.1;">Get help now</S>
          <S css="display:block;font-size:17px;line-height:1.45;margin-top:4px;color:#F6DEDC;text-wrap:pretty;">{`Aithora calls ${callFirst} first, then the care line, then 911. You can stop it at any point.`}</S>
        </S>
        <S css="flex:0 0 auto;font-size:30px;font-weight:700;">{'→'}</S>
      </Act>
      {/* One practice control, under the ladder it tests (there was a second, worded differently, up here). */}
      <D css="box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;min-width:0;">
        <D css="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
          <S css="flex:1 1 0;font-size:17px;font-weight:700;">If something happens</S>
          {to911 != null && <S css={CHIP}>{`ENDS AT 911 IN ${to911}M`}</S>}
        </D>
        <D css="font-size:16px;color:#54606C;line-height:1.5;margin-top:6px;text-wrap:pretty;">
          {e ? 'A fall, a long silence, or a crisis in conversation starts this. Each step waits for the one before it to fail.' : plain('Your ladder is not connected to this page yet (GET /athome/me/:id/emergency). The pendant and the watch still call for help.')}
        </D>
        <D css="display:flex;flex-direction:column;margin-top:8px;">
          {ladder.map((r, i) => (
            <D key={i} css="display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:9px 0;border-top:1px solid rgba(34,39,46,.07);">
              <S css={'display:flex;align-items:center;justify-content:center;width:22px;height:22px;flex:0 0 22px;border-radius:50%;font-size:16px;font-weight:700;background:' + (i === ladder.length - 1 ? 'rgba(179,38,30,.16);color:#B3261E;' : 'rgba(79,138,214,.16);color:#264C7E;')}>{String(i + 1)}</S>
              <S css="flex:1 1 0;min-width:0;">
                <S css="display:block;font-size:16px;font-weight:600;color:#22272E;">{r.who}</S>
                <S css="display:block;font-size:16px;color:#54606C;margin-top:2px;">{r.how}</S>
              </S>
              <S css="display:flex;align-items:center;gap:6px;flex:0 0 auto;">
                <Act css={STEP_BTN} onClick={() => bump(i, -1)} disabled={c.readOnly} reason={c.reason} label={`Shorter wait for step ${i + 1}`}>
                  {'−'}
                </Act>
                <S css="font-size:16px;font-weight:700;color:#22272E;min-width:56px;text-align:center;">{r.waitMinutes === 0 ? 'at once' : `+${r.waitMinutes}m`}</S>
                <Act css={STEP_BTN} onClick={() => bump(i, 1)} disabled={c.readOnly} reason={c.reason} label={`Longer wait for step ${i + 1}`}>
                  +
                </Act>
              </S>
            </D>
          ))}
        </D>
        <D css="display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-top:12px;padding-top:12px;border-top:1px solid rgba(34,39,46,.09);">
          <Act css={BTN(!tested)} onClick={test} disabled={c.readOnly || !e} reason={c.reason ?? 'The ladder is not connected yet.'}>
            {tested ? 'Test sent' : 'Test this now'}
          </Act>
          <S css="flex:1 1 160px;min-width:0;font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;">
            {tested ? 'A test alert went to every step except 911. Anyone who does not acknowledge within an hour will show here as unreachable.' : 'Rings every step except 911 and tells them it is only a test, so you find out now whether they answer. Nothing here is proven until it has been run once.'}
          </S>
        </D>
      </D>
      <D css="box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;min-width:0;">
        <D css="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
          <S css="flex:1 1 0;font-size:17px;font-weight:700;">Plan while you are well</S>
        </D>
        <D css="font-size:16px;color:#54606C;line-height:1.5;margin-top:6px;text-wrap:pretty;">Deciding in the two days after a hospital stay costs more and chooses worse. These are worth settling now.</D>
        {(e?.planning ?? []).map((p) => (
          <D key={p.id} css="display:flex;align-items:center;gap:12px;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);">
            <S css={markCss(p.done)}>{p.done ? '✓' : ''}</S>
            <S css="flex:1 1 0;min-width:0;">
              <S css="display:block;font-size:16px;color:#22272E;">{p.name}</S>
              <S css="display:block;font-size:16px;color:#54606C;margin-top:2px;">{p.note}</S>
            </S>
            <Act
              css={BTN(!p.done) + (p.done ? 'opacity:.55;' : '')}
              onClick={() =>
                c.openSheet({
                  kicker: p.done ? 'IN PLACE' : 'PLAN AHEAD',
                  title: p.name,
                  glyph: p.done ? '✓' : '✎',
                  color: p.done ? '#12503C' : '#2F5C97',
                  body: p.note,
                  rows: p.details.map((x, i) => ({ n: String(i + 1), k: x.k, v: x.v })),
                  primary: p.done ? 'View the document' : `Start now with ${callFirst}`,
                  onPrimary: () => c.act({ kind: 'planning', target: p.id }),
                })
              }
            >
              {p.done ? 'Done' : 'Start'}
            </Act>
          </D>
        ))}
      </D>
    </D>
  );
}
